import { useMemo } from "react";
import { Platform } from "react-native";

import {
  getButtonTextColorStyles,
  getButtonStyles,
  getButtonIconColorStyles,
} from "./styles";
import { useTheme } from "../../utils";

import type { ButtonColors, ButtonSize, ButtonVariation } from "./types";
import type { StyleProp, ViewStyle } from "react-native";

interface useButtonStylesArgs {
  variation: ButtonVariation;
  color: ButtonColors;
  disabled: boolean;
  size?: ButtonSize;
  isLoading: boolean;
}

export function useButtonStyles({
  variation,
  color,
  disabled,
  size,
  isLoading,
}: useButtonStylesArgs) {
  const { colors, spacing, isLightMode, shadows } = useTheme();

  const normalizedSize = useMemo<ButtonSize>(() => {
    return size
      ? size
      : Platform.select<ButtonSize>({
          web: "small",
          default: "large",
        });
  }, [size]);

  const sizeStyles = useMemo(() => {
    switch (normalizedSize) {
      case "large": {
        return {
          paddingVertical: spacing.md,
          paddingHorizontal: spacing.xl,
        };
      }
      case "x-small": {
        return {
          paddingVertical: 2,
          paddingHorizontal: 24,
        };
      }
      // defaults to the medium size
      default: {
        return {
          paddingVertical: spacing.xs,
          paddingHorizontal: spacing.xl,
        };
      }
    }
  }, [normalizedSize, spacing]);

  const textColorStyles = useMemo<{
    normal: { color: string };
    // used for pressed and hover states
    active?: { color: string };
  }>(() => {
    return getButtonTextColorStyles({
      variation,
      color,
      disabled,
      colors,
      isLightMode,
      isLoading,
    });
  }, [variation, color, disabled, colors, isLightMode, isLoading]);

  const textSizeStyles = useMemo(() => {
    return {
      fontSize: 16,
      lineHeight: 24,
    };
  }, []);

  const buttonStyles = useMemo<{
    normal: ViewStyle;
    // used for pressed and hover states
    active?: ViewStyle;
  }>(() => {
    return getButtonStyles({
      variation,
      color,
      disabled,
      colors,
      shadows,
      isLightMode,
      isLoading,
    });
  }, [variation, disabled, color, colors, shadows, isLightMode, isLoading]);

  const iconColorStyles = useMemo(
    () =>
      getButtonIconColorStyles({
        variation,
        color,
        disabled,
        colors,
        isLightMode,
        isLoading,
      }),
    [variation, color, disabled, colors, isLightMode, isLoading]
  );

  const activityIndicatorStyles: StyleProp<ViewStyle>[] = useMemo(() => {
    return [
      {
        position: "absolute",
      },
      Platform.OS === "web"
        ? {
            top: "50%",
            left: "50%",
            transform: [{ translateX: -10 }, { translateY: -10 }],
          }
        : null,
    ];
  }, []);

  return {
    sizeStyles,
    textColorStyles,
    textSizeStyles,
    buttonStyles,
    iconColorStyles,
    activityIndicatorStyles,
  };
}
